import {SagaIterator} from 'redux-saga';

import {call, put, select, takeEvery, takeLatest} from 'redux-saga/effects';

import {ApplicationStoreType} from 'desktop/store';
import {AddressType, CurrentAddressType} from 'desktop/types/addressType';

import {ActionType} from 'types/reducers';

import {resaveDelivery} from '../../../reducers/checkout/checkout';
import {
    restoreAddressesAfterCanceledEditing,
    startAddNewCustomerAddressSaga,
    startSavingCustomerAddressSaga,
    startUpdateCustomerAddressSaga,
} from '../../../reducers/deliveryStep/customerAddresses/customerAddresses';
import {setEditStepLoading} from '../../../reducers/editStep/editStep';
import {
    finishDeliveryStep,
    saveDataForDeliveryStepWithoutValid,
    startDeliveryStepSaga,
} from '../../../reducers/sagaStep/sagaStep';
import {
    getAddressesSameness,
    getCustomerAddresses,
} from '../../../selectors/deliveryStep/deliveryStep';

import {addNewCustomerAddressSaga} from '../addNewCustomerAddressSaga/addNewCustomerAddressSaga';
import {deliveryStepSaga} from '../deliveryStepSaga/deliveryStepSaga';
import {editSavedCustomerAddressSaga} from '../editSavedCustomerAddressSaga/editSavedCustomerAddressSaga';
import {
    finishDeliveryStepInitSaga,
    saveDataFromDeliveryStepOrSwitchNextStep,
} from '../finishDeliveryStepSaga/finishDeliveryStepSaga';
import {resaveDeliverySaga} from '../resaveDeliverySaga/resaveDeliverySaga';
import {restoreAddressesAfterCanceledEditingIfNeededSaga} from '../restoreAddressDataSaga/restoreAddressDataSaga';
import {updateCustomerAddressSaga} from '../updateCustomerAddressSaga/updateCustomerAddressSaga';

import {ADDRESSES} from './constants';

export type AddressesType = {
    [key: string]: {
        oppositeValue: CurrentAddressType;
        selector: (store: ApplicationStoreType) => AddressType;
        updater: (address: AddressType) => void;
    };
};

/**
 * Perform checking that we actually should add new address
 * @param {boolean} addressesIsSame - Addresses sameness parameter
 * @param {string} type - Type of edited address
 * @return {SagaIterator} Should we add new address instead of editing current
 */
export function* shouldAddANewAddress(addressesIsSame: boolean, type: string): SagaIterator {
    const {
        [`${ADDRESSES[type].oppositeValue}_address_id`]: oppositeAddressId,
        [`${type}_address_id`]: editedAddressId,
    } = yield select(getCustomerAddresses);
    const addressIDsIsSame = editedAddressId === oppositeAddressId;

    return !addressesIsSame && addressIDsIsSame;
}

/**
 * Determine behavior for edited address. Should we add new address or edit current. Actual for editing address process.
 * @param {CurrentAddressType} action - Received action with behaviour data
 */
export function* determineBehaviourForEditedAddress(
    action: ActionType<CurrentAddressType>,
): SagaIterator {
    yield put(setEditStepLoading(true));
    const editedAddress = yield select(ADDRESSES[action.payload].selector);
    const isAddressesSame = yield select(getAddressesSameness);

    const addNewAddressInsteadOfEditCurrent = yield call(
        shouldAddANewAddress,
        isAddressesSame,
        action.payload,
    );

    if (addNewAddressInsteadOfEditCurrent) {
        yield call(addNewCustomerAddressSaga, action);
    } else {
        yield call(editSavedCustomerAddressSaga, editedAddress, {
            isAddressesSame,
            type: action.payload,
        });
    }
}

/**
 * Begin of saga
 */
export function* checkoutDeliveryStepSaga(): SagaIterator {
    yield takeLatest(`${startDeliveryStepSaga}`, deliveryStepSaga);
    yield takeLatest(`${startUpdateCustomerAddressSaga}`, updateCustomerAddressSaga);
    yield takeEvery(
        `${saveDataForDeliveryStepWithoutValid}`,
        saveDataFromDeliveryStepOrSwitchNextStep,
    );
    yield takeEvery(
        `${restoreAddressesAfterCanceledEditing}`,
        restoreAddressesAfterCanceledEditingIfNeededSaga,
    );
    yield takeLatest(`${finishDeliveryStep}`, finishDeliveryStepInitSaga);
    yield takeLatest(`${startSavingCustomerAddressSaga}`, determineBehaviourForEditedAddress);
    yield takeLatest(`${startAddNewCustomerAddressSaga}`, addNewCustomerAddressSaga);
    yield takeLatest(`${resaveDelivery}`, resaveDeliverySaga);
}
